"use client";

import { useCallback, useState } from "react";

import {
	addToCart,
	type AddToCartActionRequest,
	type AddToCartActionStatus
} from "@/app/actions/cart";
import { useCartStore, useSessionStore } from "@/app/store";
import { getAddToCartErrorMessage } from "./errorMessages";

// Adds to cart via the action; binds the failure status to user-facing `error`.
export function useAddToCart(payload: AddToCartActionRequest): {
	cartQuantity: number;
	handleClick: () => Promise<void>;
	loading: boolean;
	error: string | null;
	errorStatus: AddToCartActionStatus | null;
	submitted: boolean;
} {
	const sessionState = useSessionStore((state) => state.sessionState);
	const cartState = useCartStore((state) => state.cartState);
	const setCartState = useCartStore((state) => state.setCartState);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [errorStatus, setErrorStatus] =
		useState<AddToCartActionStatus | null>(null);
	const [submitted, setSubmitted] = useState(false);

	const cartQuantity =
		cartState.status === "connected"
			? cartState.entries.reduce((acc, entry) => acc + entry.quantity, 0)
			: 0;

	const failWith = useCallback((status: AddToCartActionStatus) => {
		setErrorStatus(status);
		setError(getAddToCartErrorMessage(status));
	}, []);

	const handleClick = useCallback(async () => {
		setLoading(true);
		setError(null);
		setErrorStatus(null);

		try {
			const response = await addToCart(sessionState, payload);

			if (response.status !== "success") {
				failWith(response.status);
				return;
			}

			setSubmitted(true);
			setCartState(response.cart);

			setTimeout(() => setSubmitted(false), 1000);
		} catch {
			failWith("network-error");
		} finally {
			setLoading(false);
		}
	}, [payload, sessionState, setCartState, failWith]);

	return {
		cartQuantity,
		handleClick,
		loading,
		error,
		errorStatus,
		submitted
	};
}
